import { useState, useCallback, useEffect, useMemo } from 'react';
import { Avatar, Box, Container, Button, IconButton } from '@mui/material';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import toast from 'react-hot-toast';
import { httpRequest } from 'src/services/httpRequest';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch, useSelector } from 'react-redux';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { useParams, useNavigate } from 'react-router-dom';
import { RootState } from 'src/redux-store/appStore';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import { FC } from 'react';
import { getLocalData } from 'src/utils/CommonFunctions';
import { PetDetails } from 'src/types/PetPermit/petPermit.type';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}));

interface AddNewPetProps {
  setOpen: (open: boolean) => void;
  open: boolean;
  petDetails?: PetDetails;
  returnAfterSavePath?: string | null;
  onClearReturnAfterSave?: () => void;
}

const AddNewPet: FC<AddNewPetProps> = ({
  setOpen,
  open,
  petDetails,
  returnAfterSavePath = null,
  onClearReturnAfterSave
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { permit } = useParams();
  const [loading, setLoading] = useState(false);
  const currentPermitRole = useSelector(
    (state: RootState) =>
      state?.currentPermit?.currentPermitDetails?.currentPermitUserRole
  );
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const isManagerOrAdmin =
    currentPermitRole === 'Manager' ||
    userData?.UserRole === 'Admin' ||
    userData?.UserRole === 'Super Admin';
  const loggedInOwnerName = `${userData?.FirstName ?? ''} ${userData?.LastName ?? ''}`.trim();
  const loggedInOwnerEmail = String(userData?.EmailID ?? '').trim();
  const [avatar, setAvatar] = useState<string | null>(petDetails?.ProfilePicture);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
  const petCount = getLocalData('petCount');

  // Keep avatar in sync when editing an existing pet that already has a profile picture
  useEffect(() => {
    if (open && petDetails?.ProfilePicture && !profilePictureFile) {
      setAvatar(petDetails.ProfilePicture);
    }
  }, [open, petDetails?.ProfilePicture, profilePictureFile]);

  //Method to handle image upload
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Store the file object for later upload
      setProfilePictureFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatar(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  //Form fields using FormikForm - IsSpayedNeutered disabled for regular users when editing
  const formFields = useMemo(
    () => [
    {
      fieldName: 'PetType',
      initialValue: petDetails?.PetType,
      fieldLabel: 'Pet Type',
      fieldType: 'select',
      options: ['Dog', 'Cat'],
      required: true
    },
    {
      fieldName: 'PetName',
      initialValue: petDetails?.PetName,
      fieldLabel: 'Pet Name',
      fieldType: 'text',
      required: true
    },
    {
      fieldName: 'OwnerName',
      initialValue:
        petDetails?.OwnerName ||
        (!isManagerOrAdmin ? loggedInOwnerName : '') ||
        '',
      fieldLabel: 'Owner Name',
      fieldType: 'text',
      required: true
    },
    {
      fieldName: 'OwnerEmail',
      initialValue:
        petDetails?.OwnerEmail ||
        (!isManagerOrAdmin ? loggedInOwnerEmail : '') ||
        '',
      fieldLabel: 'Owner Email',
      fieldType: 'email',
      required: false
    },
    {
      fieldName: 'OwnerContactNumber',
      initialValue: petDetails?.OwnerContactNumber,
      fieldLabel: 'Owner Phone',
      fieldType: 'mobile',
      required: false
    },
    {
      fieldName: 'PetDOB',
      initialValue: (() => {
        const dob = petDetails?.PetDOB;
        if (!dob) return null;
        const d = new Date(dob);
        return !isNaN(d.getTime()) && d.getTime() !== 0 ? dob : null;
      })(),
      fieldLabel: 'Pet DOB',
      fieldType: 'pastDate',
      required: false
    },
    {
      fieldName: 'PetBreed',
      initialValue: petDetails?.PetBreed,
      fieldLabel: 'Pet Breed',
      fieldType: 'text',
      required: false
    },
    {
      fieldName: 'PetColor',
      initialValue: petDetails?.PetColor,
      fieldLabel: 'Pet Color',
      fieldType: 'text',
      required: false
    },
    {
      fieldName: 'PetHair',
      initialValue: petDetails?.PetHair,
      fieldLabel: 'Pet Hair',
      fieldType: 'select',
      options: ['Short', 'Medium', 'Long'],
      required: false
    },
    {
      fieldName: 'PetWeight',
      initialValue: petDetails?.PetWeight,
      fieldLabel: 'Pet Weight (Lbs)',
      fieldType: 'number',
      required: false
    },
    {
      fieldName: 'ChipNumber',
      initialValue: petDetails?.ChipNumber,
      fieldLabel: 'Chip Number',
      fieldType: 'number'
    },
    {
      fieldName: 'PetSex',
      initialValue: petDetails?.PetSex,
      fieldLabel: 'Pet Sex',
      fieldType: 'radio',
      options: [
        { label: 'Male', value: 'Male' },
        { label: 'Female', value: 'Female' }
      ],
      required: true
    },
    {
      fieldName: 'IsSpayedNeutered',
      initialValue: petDetails?.IsSpayedNeutered ? 'Yes' : 'No',
      fieldLabel: 'Spayed/Neutered',
      fieldType: 'radio',
      options: [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' }
      ],
      required: true,
      disabled: petDetails != null && !isManagerOrAdmin
    }
  ],
    [petDetails, isManagerOrAdmin, loggedInOwnerName, loggedInOwnerEmail]
  );

  //Method to handle close
  const handleClose = () => {
    onClearReturnAfterSave?.();
    setOpen(false);
    // Reset profile picture when closing
    setAvatar(null);
    setProfilePictureFile(null);
  };

  //Method to cancel
  const handleCancel = useCallback(() => {
    handleClose();
  }, [permit]);

  //Method to upload profile picture
  const handleProfilePictureUpload = useCallback(
    async (petId: number) => {
      if (!profilePictureFile) {
        return null;
      }

      try {
        const formData = new FormData();
        formData.append('PetID', petId.toString());
        formData.append('profilePicture', profilePictureFile);

        const response = await httpRequest(
          'POST',
          '/pet_application/petDetails/upload-profile-picture',
          formData,
          {
            'Content-Type': 'multipart/form-data'
          }
        );

        if (response?.status === 'success') {
          toast.success('Profile picture uploaded successfully');
          return response.data;
        } else {
          throw new Error(response?.message || 'Profile picture upload failed');
        }
      } catch (error) {
        toast.error(error.message || 'Failed to upload profile picture');
        dispatch(handleError(error.message || 'Failed to upload profile picture'));
        throw error;
      }
    },
    [profilePictureFile, dispatch]
  );

  //Method to handle submission
  const handleSubmission = useCallback(
    async (data, method, URL: string) => {
      let navigatedAfterCreate = false;
      try {
        setLoading(true);

        // First, save/update the pet details
        const response = await httpRequest(method, URL, {
          ...data,
          IsSpayedNeutered: data.IsSpayedNeutered === 'Yes'
        });

        if (response?.status !== 'success') {
          throw new Error(response?.message || 'Submission failed');
        }

        // Get the PetID from response (for new pets) or use existing PetID (for updates)
        const petId = response.data?.PetID || response.data?.data?.PetID || petDetails?.PetID;

        // If profile picture is uploaded, call the separate API
        if (profilePictureFile && petId) {
          try {
            await handleProfilePictureUpload(petId);
          } catch (uploadError) {
            // If profile picture upload fails, still show success for pet registration
            // but log the error
            console.error('Profile picture upload failed:', uploadError);
          }
        }

        toast.success(response.message || 'Submission successful');
        localStorage.setItem('petCount', petCount + 1);

        if (
          returnAfterSavePath &&
          typeof returnAfterSavePath === 'string' &&
          method === 'POST'
        ) {
          setOpen(false);
          setAvatar(null);
          setProfilePictureFile(null);
          navigate(returnAfterSavePath, {
            state: { refreshPetList: true }
          });
          navigatedAfterCreate = true;
        }
      } catch (error) {
        toast.error(error.message || 'An error occurred');
        dispatch(handleError(error.message || 'An error occurred'));
      } finally {
        setLoading(false);
      }
      if (!navigatedAfterCreate) {
        handleClose();
      }
    },
    [
      dispatch,
      profilePictureFile,
      handleProfilePictureUpload,
      petDetails,
      returnAfterSavePath,
      navigate,
      setOpen,
      petCount,
      handleClose
    ]
  );

  return loading ? (
    <LoadingOverlay />
  ) : (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <DialogTitle id="customized-dialog-title">
        {petDetails === null || petDetails === undefined ? 'Add New Pet' : 'Update Pet'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500]
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        sx={{ p: '0px !important', maxWidth: '1000px !important' }}
        dividers
      >
        <Container>
          <Box sx={{ marginY: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: 2,
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Avatar
                alt="Pet Avatar"
                src={avatar || '/static/images/avatar/default.png'}
                sx={{
                  width: 100,
                  height: 100,
                  border: '2px solid #1976d2',
                  marginBottom: 1
                }}
              />
              <Button variant="contained" component="label">
                Upload Profile
                <input
                  type="file"
                  hidden
                  accept="image/*,.heic"
                  onChange={handleImageUpload}
                />
              </Button>
            </Box>
            <FormikForm
              allFields={formFields}
              buttonPlacement="right"
              buttonTitle={[
                'Cancel',
                petDetails === null || petDetails === undefined
                  ? 'Save'
                  : 'Update'
              ]}
              defaultEnabledButton={[
                petDetails === null || petDetails === undefined
                  ? 'Save'
                  : 'Update'
              ]}
              formMethod={(val) =>
                handleSubmission(
                  val,
                  petDetails === null || petDetails === undefined
                    ? 'POST'
                    : 'PATCH',
                  petDetails === null || petDetails === undefined
                    ? '/pet/petDetails/registerNew'
                    : `/pet/petDetails/update/${petDetails.PetID}`
                )
              }
              heading="Pet Permit"
              handleOtherButton={handleCancel}
            />
          </Box>
        </Container>
      </DialogContent>
    </BootstrapDialog>
  );
};

export default AddNewPet;
